// src/pages/UploadPage/StoreActions/meshyDownloader.js
import { useState, useEffect } from 'react';
import meshyDownloadService from '../../../Services/MeshyGLBDownloadService.js';

const useMeshyDownloader = (stores, setStores, selectedStore, setItems, selectedItem, setSelectedItem, saveStore) => {
  const [isDownloadingInBackground, setIsDownloadingInBackground] = useState(false);

  const downloadMeshyUrlsToFirebase = async (item, storeId) => {
    try {
      const needsGLBDownload = item.glbUrl && item.glbUrl.includes('meshy.ai');
      const needsImageDownload = item.imageUrl && item.imageUrl.includes('meshy.ai');
      
      if (!needsGLBDownload && !needsImageDownload) {
        return item;
      }
      
      console.log(`📥 Auto-downloading Meshy files for: ${item.itemName}`);
      
      let newGlbUrl = item.glbUrl;
      let newImageUrl = item.imageUrl;
      
      if (needsGLBDownload) {
        const glbResult = await meshyDownloadService.downloadGLBToFirebase(
          item.glbUrl, 
          item.itemName, 
          item.id
        );
        if (glbResult.success) {
          newGlbUrl = glbResult.firebaseUrl;
        }
      }
      
      if (needsImageDownload) {
        const imageResult = await meshyDownloadService.downloadImageToFirebase(
          item.imageUrl, 
          item.itemName, 
          item.id
        );
        if (imageResult.success) {
          newImageUrl = imageResult.firebaseUrl;
        }
      }
      
      return {
        ...item,
        glbUrl: newGlbUrl,
        imageUrl: newImageUrl,
        downloadedToFirebase: true,
        downloadDate: new Date().toISOString()
      };
      
    } catch (error) {
      console.error(`❌ Failed to auto-download files for ${item.itemName}:`, error);
      return item;
    }
  };

  const autoDownloadAllMeshyFiles = async () => {
    if (isDownloadingInBackground) return;
    
    try {
      setIsDownloadingInBackground(true);
      
      const itemsToDownload = [];
      stores.forEach(store => {
        store.items.forEach(item => {
          const needsDownload = (item.glbUrl && item.glbUrl.includes('meshy.ai')) ||
                               (item.imageUrl && item.imageUrl.includes('meshy.ai'));
          if (needsDownload && !item.downloadedToFirebase) {
            itemsToDownload.push({ ...item, storeId: store.id });
          }
        });
      });
      
      if (itemsToDownload.length === 0) {
        console.log('✅ No Meshy files need downloading');
        return;
      }
      
      console.log(`📥 Auto-downloading ${itemsToDownload.length} Meshy files in background...`);
      
      let updatedStores = [...stores];
      let hasUpdates = false;
      
      for (const item of itemsToDownload) {
        const downloadedItem = await downloadMeshyUrlsToFirebase(item, item.storeId);
        
        const urlsChanged = downloadedItem.glbUrl !== item.glbUrl || downloadedItem.imageUrl !== item.imageUrl;
        
        if (urlsChanged) {
          hasUpdates = true;
          
          updatedStores = updatedStores.map(store => {
            if (store.id !== item.storeId) return store;
            
            return {
              ...store,
              items: store.items.map(storeItem => 
                storeItem.id === item.id ? downloadedItem : storeItem
              )
            };
          });
        }
      }
      
      if (hasUpdates) {
        console.log('💾 Saving updated URLs to database...');
        
        setStores(updatedStores);
        
        if (selectedStore) {
          const updatedSelectedStore = updatedStores.find(s => s.id === selectedStore.id);
          if (updatedSelectedStore) {
            setItems(updatedSelectedStore.items);
            
            if (selectedItem) {
              const updatedSelectedItem = updatedSelectedStore.items.find(i => i.id === selectedItem.id);
              if (updatedSelectedItem) {
                setSelectedItem(updatedSelectedItem);
              }
            }
          }
        }
        
        for (const store of updatedStores) {
          const storeToUpdate = updatedStores.find(s => s.id === store.id);
          if (storeToUpdate) {
            await saveStore(storeToUpdate);
          }
        }
        
        console.log(`✅ Auto-downloaded ${itemsToDownload.length} Meshy files to Firebase`);
      }
      
    } catch (error) {
      console.error('❌ Auto-download failed:', error);
    } finally {
      setIsDownloadingInBackground(false);
    }
  };

  useEffect(() => {
    if (stores.length > 0 && !isDownloadingInBackground) {
      const timer = setTimeout(() => {
        autoDownloadAllMeshyFiles();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [stores.length]);

  return {
    isDownloadingInBackground,
    autoDownloadAllMeshyFiles,
    downloadMeshyUrlsToFirebase,
  };
};

export default useMeshyDownloader;